import React, { useState, useEffect } from 'react';
import { requestDeleteAccountOtp, requestDeleteAccount } from '../api/actions/authactions';
import './deleteAccount.css';

export default function DeleteAccount() {
  const [step, setStep] = useState('mobile'); // mobile -> otp -> done
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleSendOtp = async (e) => {
    e && e.preventDefault();
    setError('');
    if (!/^\+?\d{10,15}$/.test(mobile.trim())) {
      setError('Enter a valid mobile number');
      return;
    }
    setLoading(true);
    try {
      const res = await requestDeleteAccountOtp(mobile.trim());
      if (res.success) {
        setStep('otp');
        setResendIn(30);
      } else {
        setError(res.message || 'Could not send OTP');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit OTP');
      return;
    }
    if (!confirmed) {
      setError('Please confirm that you understand the account deletion is permanent');
      return;
    }
    setLoading(true);
    try {
      const res = await requestDeleteAccount({ mobile: mobile.trim(), otpCode: otp, reason: reason.trim() });
      if (res.success) setStep('done');
      else setError(res.message || 'Could not submit request');
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="da-page">
      <div className="da-card">
        <h1>Delete your account</h1>

        {step !== 'done' && (
          <>
            <h3 className="da-heading">Request Account Deletion</h3>
            <p className="da-sub">
              At ZEBU Taxi, we respect your privacy and your right to control your personal
              information. If you no longer wish to use our services, you can submit a request to
              permanently delete your ZEBU Taxi account and associated personal data.
            </p>
            <p className="da-sub">
              Please complete the form below to request account deletion. We will verify your mobile
              number with an OTP before submitting your request.
            </p>
            <h2 className="da-form-title">Account Deletion Request Form</h2>
          </>
        )}

        {step === 'mobile' && (
          <form onSubmit={handleSendOtp}>
            <label>Registered Mobile Number *</label>
            <input
              type="tel"
              placeholder="Registered mobile number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              autoFocus
            />
            <div className="da-hint">
              Enter the mobile number associated with your ZEBU Taxi account.
            </div>
            {error && <div className="da-error">{error}</div>}
            <button className="da-btn" disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleSubmit}>
            <label>OTP sent to {mobile} *</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />

            <label>Reason for Deletion (Optional)</label>
            <textarea
              rows={3}
              placeholder="Tell us why you want to delete your account"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="da-hint">
              Let us know why you would like to delete your account. Your feedback may help us
              improve our services.
            </div>

            <label className="da-confirm-label">Confirmation *</label>
            <label className="da-check-row">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>
                I understand that deleting my ZEBU Taxi account is permanent and that I may lose
                access to my booking history, saved information, and other account-related
                features.
              </span>
            </label>

            {error && <div className="da-error">{error}</div>}
            <button className="da-btn da-danger" disabled={loading || !confirmed}>
              {loading ? 'Submitting...' : 'Request Account Deletion'}
            </button>
            <button
              type="button"
              className="da-link"
              disabled={resendIn > 0 || loading}
              onClick={handleSendOtp}
            >
              {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="da-done">
            <div className="da-check">✓</div>
            <h2>Request submitted</h2>
            <p>
              Our team will review your request and send a confirmation link to the email on your
              account. Please click that link to confirm. You will be notified by email once your
              request is processed.
            </p>
          </div>
        )}

        <div className="da-info">
          <h3>What Happens After You Submit Your Request?</h3>
          <p>
            Once you submit your request, our team will review it and may contact you to verify your
            identity. After verification, we will process your request in accordance with applicable
            legal requirements and our Privacy Policy.
          </p>
          <p>
            We will communicate with you if additional information is required to complete your
            request.
          </p>

          <h3>What Information Will Be Deleted?</h3>
          <p>
            When your account deletion request is completed, your ZEBU Taxi account and associated
            personal data will be deleted, subject to applicable legal requirements and legitimate
            data-retention obligations.
          </p>
          <p>
            Certain information may need to be retained for legal, regulatory, accounting,
            fraud-prevention, or dispute-resolution purposes, where permitted or required by law.
          </p>
          <p>
            Deleting your account may result in the permanent loss of access to your booking
            history, saved details, and other account-related information.
          </p>

          <h2>Need Help?</h2>
          <p>
            If you have questions about account deletion or the handling of your personal
            information, please contact the ZEBU Taxi support team using the official contact
            details published on our website.
          </p>
          <p>For further information, please read our:</p>
          <ul>
            <li>
              Privacy Policy:{' '}
              <a href="https://zebutaxi.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                https://zebutaxi.com/privacy-policy
              </a>
            </li>
            <li>
              Terms and Conditions:{' '}
              <a href="https://zebutaxi.com/terms" target="_blank" rel="noopener noreferrer">
                https://zebutaxi.com/terms
              </a>
            </li>
          </ul>

          <h3>Your Privacy Matters</h3>
          <p>
            Thank you for using ZEBU Taxi. We appreciate your trust and are committed to handling
            account deletion requests responsibly.
          </p>
        </div>
      </div>
    </div>
  );
}